import React, { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { NavigationBack } from 'uiComponents/navigation/navigationBack'
import { WithNavigationProps, withNavigation } from 'hocs'
import { resetState, selectOrder } from '../redux'
import OrderInfo from './orderInfo'
import OrderItems from './OrderItems'
import OrderTicketsActions from '../commons/orderTicketsActions'
import { useAppDispatch } from 'store/hooks'
import { useGetOrderDetailsByUuidQuery } from 'orders/reduxQueries'
import { PageLoader } from 'uiComponents/loaders'
import { FillDetailsAlert } from '../commons/orderTicketsActions/fillDetails/fillDetailsAlert'
import { useHasFeature } from 'utils/useHasFeature'
import { useMessages } from 'messagesContext'
import MembershipDetailsPanel from 'memberships/membershipDetailsPanel/membershipDetailsPanel'
import { getHashParam, getHashRoute, removeHash } from 'utils/hash'
import './index.scss'
import { useTranslation } from 'translations'

interface OrdersDetailsLocationState {
    ordersListParams: string
}

interface OrdersDetailsProps extends WithNavigationProps<{ id: string; accountSlug: string }> {}

const OrderDetails: React.FC<OrdersDetailsProps> = ({ match }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useAppDispatch()
    const { accountSlug, id } = match.params
    const location = useLocation()
    const locationState = useRef(location.state as OrdersDetailsLocationState)
    const shouldShowPricesWithoutTax = useHasFeature({ featureName: 'OrdersListPricesExcludingTax' })
    const { replaceMessages, hideMessage } = useMessages()
    const { data: order, isFetching } = useGetOrderDetailsByUuidQuery({
        uuid: id,
        include: [
            'items',
            'items.options',
            'items.barcode',
            'items.refund_info',
            'items.membership',
            'payments',
            'discounts',
            'reseller',
            'customer',
            'location',
        ],
    })

    const selectedMembershipUuid = getHashParam('membershipUuid')
    const showMembershipDetailsPanel = getHashRoute() === 'membership_details' && selectedMembershipUuid

    useEffect(() => {
        dispatch(selectOrder(order))
    }, [order])

    useEffect(() => {
        return () => {
            dispatch(resetState())
            dispatch(selectOrder(null))
        }
    }, [])

    const navigateToOrders = () => {
        const params = locationState.current?.ordersListParams
        const origin = new URLSearchParams(location.search).get('origin')

        if (origin && origin === 'daily-schedule') {
            history.goBack()
        } else {
            history.push(`/account/${accountSlug}/orders/orders${params || ''}`)
        }
    }

    if (isFetching) {
        return <PageLoader />
    }

    return (
        <div className="order-details">
            <h1>{t('orders.orders.orderDetails.title')}</h1>
            <NavigationBack onClick={navigateToOrders} text={t('orders.orders.orderDetails.backToOrders')} />
            <div className="container">
                <OrderInfo order={order} />
                <div className="action-bar">
                    <OrderTicketsActions replaceMessages={replaceMessages} hideMessage={hideMessage} />
                </div>
                <FillDetailsAlert />
                <OrderItems
                    order={order}
                    accountSlug={accountSlug}
                    shouldShowPricesWithoutTax={shouldShowPricesWithoutTax}
                />
            </div>
            {showMembershipDetailsPanel && (
                <MembershipDetailsPanel membershipUuid={selectedMembershipUuid} onClose={removeHash} />
            )}
        </div>
    )
}

export default withNavigation(OrderDetails)
